import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I get more plumbing leads in Colorado?",
    a: "The fastest wins come from Google Maps + local SEO so emergency searches like 'plumber near me' or 'water heater repair Colorado Springs' land on your business first. Pair that with a mobile site built for one-tap calling and you'll see lead volume climb within a couple of months.",
  },
  {
    q: "What does plumbing SEO cost in Colorado Springs?",
    a: "Real plumbing SEO in Colorado typically runs $1,500–$3,500/month depending on competition (Denver is more aggressive than Pueblo). We're transparent — full pricing is on our pricing page and every package is month-to-month.",
  },
  {
    q: "How do I rank in Google Maps for plumbing services?",
    a: "Optimize your Google Business Profile end-to-end (services, categories, weekly posts, photos), earn a steady flow of real Google reviews, and back it up with local citations and on-page SEO. We handle all of that as part of our Google Maps service.",
  },
  {
    q: "Do Google Ads work for plumbers in Colorado?",
    a: "Yes — for emergency plumbing especially. The intent is sky-high and a properly built campaign with call-tracking pays back fast. The trick is targeting the right service-area, the right keywords, and the right time-of-day so you're not burning budget on tire-kickers.",
  },
  {
    q: "How fast can I start getting more plumbing calls from my website?",
    a: "If your Google Business Profile and on-page SEO are weak, we usually see noticeable lift in calls within 30–60 days. Add Google Ads and you can be generating leads inside a week of launch.",
  },
];

export const Route = createFileRoute("/plumbing-marketing-colorado")({
  head: () => industryMarketingHead("Plumbing", "plumbing", "plumbing-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Plumbing"
      industryLower="plumbing"
      slug="plumbing-marketing-colorado"
      subheadline="More emergency calls, more booked repairs, more revenue — for Colorado plumbers."
      painPoints={[
        "Emergency plumbing calls going to competitors who rank higher on Google Maps",
        "Weak Google Business Profile losing Map Pack clicks to larger plumbing companies",
        "Website looks outdated and doesn't convert mobile visitors calling from search",
      ]}
      serviceDescriptions={{
        seo: "Rank for emergency plumbing, water heater, and drain searches across Colorado.",
        googleMaps: "Win the Map Pack for 'plumber near me' before your competitor does.",
        webDesign: "Mobile-first plumbing websites that turn searchers into emergency calls.",
        ppc: "Emergency-intent Google Ads with call tracking and ROI you can actually see.",
        geoAi: "Get cited by ChatGPT and Gemini when Coloradans ask for a trusted plumber.",
        socialMedia: "Local content that builds trust and earns more recurring service calls.",
      }}
      testimonial={{
        quote:
          "We went from page 4 to the top of the Map Pack in under 5 months. They truly understand the Colorado market.",
        role: "Plumbing Company Owner",
        city: "Monument, CO",
      }}
      faqs={FAQS}
      industrySpokes={[
        {
          "to": "/plumbing-seo-colorado",
          "label": "Plumbing SEO in Colorado",
          "description": "Rank for emergency plumber, water heater, and drain cleaning searches."
        },
        {
          "to": "/plumbing-google-maps-colorado",
          "label": "Plumbing Google Maps in Colorado",
          "description": "Map Pack ranking and GBP optimization for Colorado plumbers."
        },
        {
          "to": "/plumbing-web-design-colorado",
          "label": "Plumbing web design in Colorado",
          "description": "Hand-coded plumbing sites built for late-night emergency calls on mobile."
        }
      ]}
      cityHubLinks={[
        {
          "to": "/colorado-springs",
          "label": "Plumbing marketing in Colorado Springs",
          "description": "Springs-tuned plumbing SEO, Maps, and web from our local HQ."
        },
        {
          "to": "/denver-digital-marketing",
          "label": "Plumbing marketing in Denver",
          "description": "Compete in Denver's top-tier plumbing market with neighborhood-level work."
        },
        {
          "to": "/boulder-digital-marketing",
          "label": "Plumbing marketing in Boulder",
          "description": "Boulder + Front Range plumbing SEO and lead generation."
        }
      ]}
    />
  ),
});
